import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { imageProps } from "components/ImageIcon/ImageIcon";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import dotStyle from "components/ProvisioningStateDot/ProvisioningStateDot.module.scss";
import RecordLabel from "components/RecordLabel";
import { getLocationData } from "components/Table/recordTableUtils";
import { FeConfigType } from "config/config";
import { modifiedAtDto, modifiedByDto } from "config/utils";
import InlineActionButtons from "containers/Resources/components/CommonActions/InlineActionButtons";
import { nextRunsColumn } from "containers/Search/components/SearchTabs/columns";
import { Record, Schedule } from "graphql/generated/graphql";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";

const commonListQuery = `
    id
    name
    comment_count
    record_type
    modified_at
    modified_by_identity{
        identity_type
        id
        name
        is_deleted
    }
    project_id_project {
        id
        name
        icon
        record_type
    }
    bundle_id_bundle {
        id
        name
        icon
        record_type
    }`;

export const recordListQuery = (
    type
) => `query($filter: String, $order: String, $limit: Int, $offset: Int) {
    ${type}_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
        ${type}_count
        ${type}_list{
            ${commonListQuery}
            ${
                type === "webhook"
                    ? `is_enabled
                is_productive`
                    : ""
            }
            ${
                type === "custom_object"
                    ? `object_template_id_object_template {
                    id
                    name
                    }`
                    : ""
            }
        }
    },
}`;
export const customObjectUsedByListQuery = `query($id: String!){
        custom_object(id: $id) {
            referencing_custom_objects
        }
    } `;
export const settingHoldingLockListQuery = `query($id: String!){
        setting(id: $id) {
            execution_id_execution{
                id
                name
                record_type
                modified_at
                modified_by_identity{
                    identity_type
                    id
                    name
                    is_deleted
                }
                project_id_project {
                    id
                    name
                    icon
                    record_type
                }
                bundle_id_bundle {
                    id
                    name
                    icon
                    record_type
                }
            }
        }       
    }`;

export const scheduleQuery = `query($filter: String, $order: String, $limit: Int, $offset: Int) {
    schedule_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
        schedule_count
        schedule_list{
            ${commonListQuery}
            is_enabled
            next_runs
            scheduler_id_scheduler {
                id
                name
            }
            execution_plain_list_schedule_id(limit: 100, offset: 0, order: "+id") {
                id
                name
                status
            }
        }
    },
}`;

export const nameColumn = {
    label: "Name",
    key: "name",
    shown: true,
    sortable: true,
    dto: (data: Record) => {
        if (data) {
            const { id, name, record_type, comment_count } = data;
            const recordType = getTypedLowerCase(record_type);
            const isCustomObject = recordType === "custom_object";
            return (
                <div className={isCustomObject ? dotStyle.relativePositionedContainer : ""}>
                    {isCustomObject && <ProvisioningStateDot id={id} />}

                    <RecordLabel
                        name={name}
                        link={getRelativeUrl(recordType, id)}
                        iconByType={recordType}
                        commentCount={comment_count}
                        {...imageProps(recordType, id)}
                    />
                </div>
            );
        } else {
            return null;
        }
    },
};

const enabledColumn = {
    label: "Enabled",
    key: "is_enabled",
    shown: true,
    sortable: true,
    dto: (value: any) => (
        <SimpleToggle identifier="is_enabled" value={value.is_enabled} isReadOnly={true} />
    ),
};

const locationColumn = {
    label: "Location",
    key: "location",
    shown: true,
    sortable: false,
    dto: (data: Record) => {
        const location = getLocationData(data);
        if (location) {
            const locationTyped = getTypedLowerCase(location.record_type);
            return (
                <RecordLabel
                    name={location.name}
                    link={getRelativeUrl(locationTyped, location.id)}
                    iconByType={locationTyped}
                    image={location.icon}
                />
            );
        } else {
            return null;
        }
    },
};

const modifiedAtColumn = {
    label: "Modified at",
    key: "modified_at",
    shown: true,
    sortable: true,
    dto: (data) => modifiedAtDto(data),
};

const executionColumns = [nameColumn, locationColumn, modifiedAtColumn];

const scheduleColumns = [
    nameColumn,
    enabledColumn,
    {
        label: "Schedule execution",
        key: "schedule_execution",
        shown: true,
        sortable: false,
        dto: (data: Schedule, refreshAction) => {
            const recordType = "execution";
            const runningExecution = data?.execution_plain_list_schedule_id?.find(
                (execution) => execution?.status && !execution?.status.includes("ENDED")
            );
            if (runningExecution) {
                return (
                    <>
                        <RecordLabel
                            name={runningExecution.name}
                            link={getRelativeUrl(recordType, runningExecution.id)}
                            iconByType={recordType}
                        />
                        <InlineActionButtons
                            resourceData={{ ...runningExecution, resource_type: "execution" }}
                            afterButtonAction={refreshAction}
                            hideGoToParent
                        />
                    </>
                );
            } else {
                return null;
            }
        },
    },
    nextRunsColumn(),
    {
        label: "Scheduler",
        key: "scheduler_id",
        shown: true,
        sortable: false,
        dto: (data: Schedule) => {
            if (data?.scheduler_id_scheduler) {
                const {
                    scheduler_id_scheduler: { id, name },
                } = data;
                const recordType = "scheduler";
                return (
                    <RecordLabel
                        name={name}
                        link={getRelativeUrl(recordType, id)}
                        iconByType={recordType}
                    />
                );
            } else {
                return null;
            }
        },
    },
];

export const columns = (type?: FeConfigType) => {
    if (type === "schedule") {
        return scheduleColumns;
    }
    if (type === "execution") {
        return executionColumns;
    }
    const objectTemplateColumn =
        type === "custom_object"
            ? [
                  {
                      label: "Object template",
                      key: "object_template",
                      shown: true,
                      sortable: true,
                      dto: (data: any) => {
                          if (data) {
                              const { object_template_id_object_template } = data;
                              const recordType = "object_template";
                              return (
                                  <RecordLabel
                                      name={object_template_id_object_template.name}
                                      link={getRelativeUrl(
                                          recordType,
                                          object_template_id_object_template.id
                                      )}
                                      iconByType={recordType}
                                  />
                              );
                          } else {
                              return null;
                          }
                      },
                  },
              ]
            : [];
    const webhookColumns =
        type === "webhook"
            ? [
                  enabledColumn,
                  {
                      label: "Productive",
                      key: "is_productive",
                      shown: true,
                      sortable: true,
                      dto: (value: any) => (
                          <SimpleToggle
                              identifier="is_productive"
                              value={value.is_productive}
                              isReadOnly={true}
                          />
                      ),
                  },
              ]
            : [];
    return [
        nameColumn,
        ...objectTemplateColumn,
        locationColumn,
        modifiedAtColumn,
        {
            label: "Modified by",
            key: "modified_by",
            shown: true,
            sortable: true,
            dto: modifiedByDto,
        },
        ...webhookColumns,
    ];
};
